import api from '../../api'

export const quickBooksInvoicesEndpoint = '/web/admin/quickbooks-invoices'
export const quickBooksResyncEndpoint = '/web/admin/quickbooks-invoices/resync'
export const quickBooksRelinkCandidatesEndpoint = '/web/admin/quickbooks-invoices/relink-candidates'
export const quickBooksRelinkEndpoint = '/web/admin/quickbooks-invoices/relink'
export const quickBooksInvoicePaymentsEndpoint = '/web/admin/quickbooks-invoices/payments'
export const quickBooksPaymentReapplyEndpoint = '/web/admin/quickbooks-invoices/payments/reapply'
export const quickBooksPaymentRelinkCandidatesEndpoint = '/web/admin/quickbooks-invoices/payments/relink-candidates'
export const quickBooksPaymentRelinkEndpoint = '/web/admin/quickbooks-invoices/payments/relink'

export type QuickBooksInvoiceSyncStatus = {
  invoiceId: string
  invoiceNumber: string
  customerName: string
  qboInvoiceId: string
  qboDocNumber: string | null
  total: number
  balance: number
  cachedQboBalance: number | null
  lastSyncedAt: string | null
  qboInvoiceExists: boolean
  linkedToDifferentRealm: boolean
  qboPaymentNotApplied: boolean
}

export type ResyncQuickBooksInvoicesRequest = {
  tenantId: number
  invoiceId?: string
}

export type ResyncQuickBooksInvoicesResult = {
  invoiceCount: number
}

export const getQuickBooksInvoices = async (tenantId: number): Promise<QuickBooksInvoiceSyncStatus[]> => {
  return (await api.get(quickBooksInvoicesEndpoint, { params: { tenantId } })).data
}

export const resyncQuickBooksInvoices = async (
  request: ResyncQuickBooksInvoicesRequest
): Promise<ResyncQuickBooksInvoicesResult> => {
  return (await api.post(quickBooksResyncEndpoint, request)).data
}

export type QuickBooksInvoiceRelinkCandidate = {
  qboInvoiceId: string
  docNumber: string
  customerName: string
  total: number
  balance: number
  transactionDate: string | null
  linkedToInvoiceNumber: string | null
  matchesTotal: boolean
  matchesInvoiceNumber: boolean
  matchesOriginalDocNumber: boolean
  matchesPrivateNote: boolean
}

export type QuickBooksInvoiceRelinkCandidates = {
  invoiceNumber: string
  qboRealmId: string
  currentQboInvoiceId: string
  qboCustomerId: string
  qboCustomerName: string
  candidateListTruncated: boolean
  candidates: QuickBooksInvoiceRelinkCandidate[]
}

export type RelinkQuickBooksInvoiceRequest = {
  tenantId: number
  invoiceId: string
  qboInvoiceId: string
}

export type RelinkQuickBooksInvoiceResult = {
  qboInvoiceId: string
  qboRealmId: string
}

export const getRelinkCandidates = async (
  tenantId: number,
  invoiceId: string
): Promise<QuickBooksInvoiceRelinkCandidates> => {
  return (await api.get(quickBooksRelinkCandidatesEndpoint, { params: { tenantId, invoiceId } })).data
}

export const relinkQuickBooksInvoice = async (
  request: RelinkQuickBooksInvoiceRequest
): Promise<RelinkQuickBooksInvoiceResult> => {
  return (await api.post(quickBooksRelinkEndpoint, request)).data
}

export type QuickBooksPaymentMethodType = 'unknown' | 'card' | 'ach' | 'cash' | 'check' | 'validatedAch'

export type QuickBooksPaymentStatus = 'ready' | 'pending' | 'completed' | 'failed' | 'canceled'

export type QuickBooksPaymentIssue =
  | 'notExported'
  | 'deletedInQbo'
  | 'notAppliedToInvoice'
  | 'appliedToDifferentInvoice'
  | 'customerMismatch'
  | 'exportedToDifferentRealm'

export type QuickBooksPaymentReapplyBlockedReason =
  | 'notExported'
  | 'deletedInQbo'
  | 'customerMismatch'
  | 'insufficientUnappliedAmount'
  | 'alreadyApplied'
  | 'exportedToDifferentRealm'

export type QboPaymentAppliedInvoice = {
  qboInvoiceId: string
  docNumber: string
  amount: number
}

export type QboPaymentSnapshot = {
  exists: boolean
  qboCustomerId: string
  qboCustomerName: string
  total: number
  unappliedAmount: number
  appliedInvoices: QboPaymentAppliedInvoice[]
  syncToken: string
  createdAt: string | null
  lastUpdatedAt: string | null
}

export type QuickBooksPaymentDiagnostic = {
  paymentId: string
  methodType: QuickBooksPaymentMethodType
  checkNumber: string
  lastFour: string
  amount: number
  status: QuickBooksPaymentStatus
  paymentRecordedAt: string | null
  qboPaymentId: string
  qboPayment: QboPaymentSnapshot | null
  issues: QuickBooksPaymentIssue[]
  reapplyBlockedReason: QuickBooksPaymentReapplyBlockedReason | null
}

export type QuickBooksInvoicePayments = {
  invoiceId: string
  invoiceNumber: string
  qboInvoiceId: string
  qboDocNumber: string | null
  qboCustomerId: string
  qboCustomerName: string
  payments: QuickBooksPaymentDiagnostic[]
}

export const getQuickBooksInvoicePayments = async (
  tenantId: number,
  invoiceId: string
): Promise<QuickBooksInvoicePayments> => {
  return (await api.get(quickBooksInvoicePaymentsEndpoint, { params: { tenantId, invoiceId } })).data
}

export type ReapplyQuickBooksPaymentRequest = {
  tenantId: number
  invoiceId: string
  paymentId: string
  qboPaymentId: string
  syncToken: string
}

export type ReapplyQuickBooksPaymentResult = {
  qboPaymentId: string
  qboInvoiceId: string
  appliedAmount: number
  syncToken: string
}

export type QuickBooksPaymentRelinkCandidate = {
  qboPaymentId: string
  customerName: string
  total: number
  unappliedAmount: number
  transactionDate: string | null
  paymentRefNumber: string
  appliedInvoiceDocNumbers: string[]
  linkedToInvoiceNumber: string | null
  matchesAmount: boolean
  matchesNote: boolean
  matchesDate: boolean
}

export type QuickBooksPaymentRelinkCandidates = {
  paymentId: string
  currentQboPaymentId: string
  qboCustomerId: string
  qboCustomerName: string
  candidateListTruncated: boolean
  candidates: QuickBooksPaymentRelinkCandidate[]
}

export type RelinkQuickBooksPaymentRequest = {
  tenantId: number
  invoiceId: string
  paymentId: string
  qboPaymentId: string
}

export type RelinkQuickBooksPaymentResult = {
  paymentId: string
  qboPaymentId: string
}

export const reapplyQuickBooksPayment = async (
  request: ReapplyQuickBooksPaymentRequest
): Promise<ReapplyQuickBooksPaymentResult> => {
  return (await api.post(quickBooksPaymentReapplyEndpoint, request)).data
}

export const getPaymentRelinkCandidates = async (
  tenantId: number,
  invoiceId: string,
  paymentId: string
): Promise<QuickBooksPaymentRelinkCandidates> => {
  return (await api.get(quickBooksPaymentRelinkCandidatesEndpoint, { params: { tenantId, invoiceId, paymentId } })).data
}

export const relinkQuickBooksPayment = async (
  request: RelinkQuickBooksPaymentRequest
): Promise<RelinkQuickBooksPaymentResult> => {
  return (await api.post(quickBooksPaymentRelinkEndpoint, request)).data
}
